import api from "../../api/api";
import {
  classifyVisit,
  needsRecordingConsent,
  pickTodayRow,
  rowsOfBody,
  sameAppointmentId,
} from "../../utils/visitGuard";
import { requestVisitConsent, showVisitBlocked } from "./VisitConsentHost";
import { getPracticeId } from "../../config/practice";

/* The recording-start gate shared by every entry point (chart mic, recording
   nudge, RecordingContext.startRecording, Editscreen voice/section/combine
   recording). Call it BEFORE any microphone, WebSocket, upload or check-in.

   - Today's visit: allowed at once, no network call, no dialog.
   - Any other visit (past, future, unknown date, or no visit):
       * the patient has a non-cancelled visit today -> blocked with an
         explanation (the chart should be on today's visit; the server refuses
         this too);
       * otherwise -> the two-step consent; allowed only after both steps, and
         the caller then sends past_visit_confirmed=true to BlinkScribe.
   If the today-visit lookup fails the consent still applies; BlinkScribe's
   own check backs it up.

   subject: "chart" (default) or "note" (the note editor) - only the wording.
   Returns { allowed, pastVisitConfirmed, info, reason }. */
export const guardRecordingVisit = async (row, { now = new Date(), subject = "chart" } = {}) => {
  const info = classifyVisit(row, now);
  if (!needsRecordingConsent(info)) {
    return { allowed: true, pastVisitConfirmed: false, info, reason: "today" };
  }
  const pid = row?.patientid;
  if (pid !== undefined && pid !== null && pid !== "") {
    let todayAppt = null;
    try {
      const practiceid = getPracticeId();
      const body = await api.get(`/v1/${practiceid}/appointments/${pid}/future`);
      todayAppt = pickTodayRow(rowsOfBody(body), pid, now);
    } catch (_) {
      todayAppt = null;
    }
    // Today's visit IS this row's own appointment (its date was unknown or
    // stale on the row): that is today's visit, never blocked.
    if (todayAppt && sameAppointmentId(todayAppt.appointmentid, row?.appointmentid)) {
      return { allowed: true, pastVisitConfirmed: false, info: classifyVisit({ ...row, ...todayAppt }, now), reason: "today" };
    }
    if (todayAppt) {
      await showVisitBlocked(info, classifyVisit(todayAppt, now), { subject });
      return { allowed: false, pastVisitConfirmed: false, info, reason: "today-exists" };
    }
  }
  const ok = await requestVisitConsent(info, { subject });
  return { allowed: ok, pastVisitConfirmed: ok, info, reason: ok ? "confirmed" : "declined" };
};
